import * as React from "react";
import { RecentlyBookedHotelsProps } from "./typings";
import {
  UitkCard,
  UitkCardContent,
  UitkCardImageLayout,
  UitkCardLink,
  UitkCardMedia,
  UitkCardTextSegment
} from "uitk-react-cards";
import TruncatedText from "shiitake";
import { UitkRating } from "uitk-react-rating";
import { ILocalization, LocalizedText } from "bernie-l10n";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";

export const RecentlyBookedHotels = (props: RecentlyBookedHotelsProps) => {
  const flexComponent = props.flexComponent;
  const model = flexComponent && flexComponent.model;
  const items = (model && model.items && model.items.filter(item => item)) || [];

  if (!model || items.length === 0) {
    return null;
  }

  return (
    <Viewport>
      <ViewMedium>
        <div
          className="RecentlyBookedHotels all-cell-1-1 uitk-cell all-y-padding-three"
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          data-fm-title-id={flexComponent.fmTitleId}
        >
          <div className="carouselMediumContainer">
            <h2 className="uitk-type-heading-600">{flexComponent.model.title}</h2>
            <IsomorphicCarousel
              itemsVisible={{
                lg: 2,
                md: 1,
                sm: 1
              }}
              buttonText={{
                nextButton: props.l10n.formatText("carousel.item.next"),
                prevButton: props.l10n.formatText("carousel.item.prev")
              }}
              pageBy={1}
              peekWidth={50}
              wrapItems={false}
            >
              {items.map((recentlyBookedHotel, index) => {
                return (
                  <li key={recentlyBookedHotel.name}>
                    <div>
                      <RecentlyBookedHotelCard
                        hotelName={recentlyBookedHotel.name}
                        hisUrl={recentlyBookedHotel.url}
                        isMobile={false}
                        starRating={recentlyBookedHotel.rating}
                        lastBookedMessage={formatLastBookedMessage(
                          recentlyBookedHotel.bookedAtNumber,
                          recentlyBookedHotel.bookedAtKey,
                          props.l10n
                        )}
                        l10n={props.l10n}
                        cardIndex={index}
                        imageUrl={recentlyBookedHotel.hotelImageUrl}
                      />
                    </div>
                  </li>
                );
              })}
            </IsomorphicCarousel>
          </div>
        </div>
      </ViewMedium>
      <ViewSmall>
        <div
          className="RecentlyBookedHotels all-cell-1-1 all-y-padding-three uitk-cell"
          id={flexComponent.id}
          data-fm={flexComponent.fmId}
          data-fm-title-id={flexComponent.fmTitleId}
        >
          <h2 className="uitk-type-heading-600 all-b-margin-three">{flexComponent.model.title}</h2>
          <div className="uitk-grid all-cell-1-1 all-x-gutter-three all-y-gutter-three">
            {items.map((recentlyBookedHotel, index) => {
              return (
                <div key={recentlyBookedHotel.name} className="uitk-cell">
                  <RecentlyBookedHotelCard
                    hotelName={recentlyBookedHotel.name}
                    hisUrl={recentlyBookedHotel.url}
                    isMobile={true}
                    starRating={recentlyBookedHotel.rating}
                    lastBookedMessage={formatLastBookedMessage(
                      recentlyBookedHotel.bookedAtNumber,
                      recentlyBookedHotel.bookedAtKey,
                      props.l10n
                    )}
                    l10n={props.l10n}
                    cardIndex={index}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </ViewSmall>
    </Viewport>
  );
};

interface RecentlyBookedHotelsCardProps {
  hotelName: string;
  hisUrl: string;
  isMobile: boolean;
  starRating: string;
  lastBookedMessage: string;
  l10n: ILocalization;
  cardIndex: number;
  imageUrl?: string;
}
const RecentlyBookedHotelCard = (props: RecentlyBookedHotelsCardProps) => {
  return (
    <UitkCard
      className="RecentlyBookedHotelsCard uitk-grid"
      imageLayout={UitkCardImageLayout.LEFT_FULLBLEED}
      withBorder={!props.isMobile}
    >
      {!props.isMobile && (
        <UitkCardMedia pxWidth={28}>
          <UitkFigure className="all-image-fit-cover" ratio={UitkFigureAspectRatioType.R1_1}>
            <LazyLoadedImage src={props.imageUrl} alt={props.hotelName} />
          </UitkFigure>
        </UitkCardMedia>
      )}
      <UitkCardContent className="uitk-cell">
        <UitkCardTextSegment className="uitk-flex uitk-flex-column uitk-flex-justify-content-space-between">
          <div>
            <TruncatedText
              lines={2}
              renderFullOnServer={true}
              overflowNode={"\u2026"}
              className="hotelTitle uitk-type-heading-300"
              tagName="h3"
            >
              {props.hotelName}
            </TruncatedText>
            <UitkRating rating={props.starRating} />
          </div>
          <p className="uitk-type-300-heading">
            <span>{props.lastBookedMessage}</span>
          </p>
        </UitkCardTextSegment>
        <UitkCardLink>
          <TrackedLink
            moduleName="recently-booked-hotels"
            href={props.hisUrl}
            rfrr={`card-${props.cardIndex}`}
          >
            <LocalizedText message="hotels.hotel.open.page" data={[props.hotelName]} />
          </TrackedLink>
        </UitkCardLink>
      </UitkCardContent>
    </UitkCard>
  );
};

const formatLastBookedMessage = (
  lastBookedNumber: number,
  lastBookedLocKey: string,
  l10n: ILocalization
): string => {
  let locKey: string = "";
  switch (lastBookedLocKey) {
    case "hotels.hotel.listing.minute": {
      locKey = "recentlyBookedHotels.lastBooking.minute";
      break;
    }
    case "hotels.hotel.listing.minutes": {
      locKey = "recentlyBookedHotels.lastBooking.minutes";
      break;
    }
    case "hotels.hotel.listing.hour": {
      locKey = "recentlyBookedHotels.lastBooking.hour";
      break;
    }
    case "hotels.hotel.listing.hours": {
      locKey = "recentlyBookedHotels.lastBooking.hours";
      break;
    }
  }

  return l10n.formatText(locKey, lastBookedNumber);
};

export default RecentlyBookedHotels;
